// src/pages/bookingWizard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/booking.css";
import { getData, postData } from "../api";
import { useSearchParams } from "react-router-dom";

const STEPS = ["Personal Information", "Package Information", "Email Verification", "Payment"];
const OTP_LEN = 6; // set to 4 if your backend uses 4-digit OTPs

// Build and submit a plain HTML form (POST) to Webxpay
function submitWebxpayForm(actionUrl, fields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = actionUrl;
  form.style.display = "none";
  Object.entries(fields || {}).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v == null ? "" : String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

function BookingWizard() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [bookingId, setBookingId] = useState(null);
  const [quote, setQuote] = useState(null); // { currency, grossAmount }

  const [searchParams] = useSearchParams();

  // Step 1
  const [p1, setP1] = useState({
    visitorType: "local", // "local" | "foreign"
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
  });

  // Step 2
  const [p2, setP2] = useState({
    date: "",
    time: "",
    packageId: "",
    adults: 1,
    children: 0,
    hasUnder4: false,
    under4Count: 0,
  });

  // Step 3 (OTP)
  const [otp, setOtp] = useState(Array(OTP_LEN).fill(""));
  const [resendTimer, setResendTimer] = useState(300); // 5 minutes
  const inputsRef = useRef([]);

  const [submitted, setSubmitted] = useState(false);

  // Load packages & preselect from ?package=CODE
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getData("packages?page=1&limit=10&sort=-createdAt,name");
        const list = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        if (!alive) return;
        setPackages(list);

        const code = searchParams.get("package");
        if (code && list.length) {
          const match = list.find((p) => String(p.code).toUpperCase() === code.toUpperCase());
          setSelectedPackage(match || null);
          setP2((f) => ({ ...f, packageId: match?.code || f.packageId, time: "" }));
        } else if (list.length && !selectedPackage) {
          setSelectedPackage(list[0]);
          setP2((f) => ({ ...f, packageId: list[0].code, time: "" }));
        }
      } catch (e) {
        console.error(e);
        if (alive) setErr("Failed to load packages.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [searchParams]);

  // Keep selection synced if URL changes after load
  useEffect(() => {
    const code = searchParams.get("package");
    if (code && packages.length) {
      const match = packages.find((p) => String(p.code).toUpperCase() === code.toUpperCase());
      setSelectedPackage(match || null);
      setP2((f) => ({ ...f, packageId: match?.code || f.packageId, time: "" }));
    }
  }, [packages, searchParams]);

  const handleP1 = (e) => setP1((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleP2 = (e) => {
    const { name, value } = e.target;
    if (name === "packageId") {
      const pkg = packages.find((p) => p.code === value) || null;
      setSelectedPackage(pkg);
      setP2((f) => ({ ...f, packageId: value, time: "" }));
    } else {
      setP2((f) => ({
        ...f,
        [name]: name === "adults" || name === "children" ? Number(value) : value,
      }));
    }
  };

  const inc = (key) => setP2((f) => ({ ...f, [key]: Math.min(f[key] + 1, 99) }));
  const dec = (key) => setP2((f) => ({ ...f, [key]: Math.max(f[key] - 1, key === "adults" ? 1 : 0) }));

  // OTP helpers
  const onOtpChange = (idx, val) => {
    const v = val.replace(/\D/g, "").slice(0, 1);
    setOtp((arr) => {
      const a = [...arr];
      a[idx] = v;
      return a;
    });
    if (v && idx < OTP_LEN - 1) inputsRef.current[idx + 1]?.focus();
  };
  const onOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };

  // Build ages for API: >=4 charged (use 5), <4 free (use 3)
  const buildChildrenAges = () => {
    const chargedCount = Math.max(p2.children - (p2.hasUnder4 ? p2.under4Count : 0), 0);
    const freeCount = p2.hasUnder4 ? Math.min(p2.under4Count, p2.children) : 0;
    return [...Array(chargedCount).fill(5), ...Array(freeCount).fill(3)];
  };

  // API calls
  const createDraftBooking = async () => {
    const payload = {
      isLocal: p1.visitorType === "local",
      firstName: p1.firstName,
      lastName: p1.lastName,
      email: p1.email,
      phone: p1.contactNumber,
      bookingDate: p2.date, // YYYY-MM-DD
      slotKey: p2.time, // e.g., HIGH_TEA_PM
      packageCode: selectedPackage?.code, // HIGH_TEA | LUNCH | ...
      adults: Number(p2.adults) || 1,
      childrenAges: buildChildrenAges(),
    };
    const res = await postData("bookings", payload); // { id, status, grossAmount, currency }
    setBookingId(res.id);
    setQuote({ currency: res.currency, grossAmount: res.grossAmount });
    return res;
  };

  const requestOtp = async () => {
    await postData("bookings/otp/request", { email: p1.email });
    setOtp(Array(OTP_LEN).fill(""));
    inputsRef.current[0]?.focus();
    setResendTimer(300);
  };

  const verifyOtp = async () => {
    const code = otp.join("");
    await postData("bookings/otp/verify", { email: p1.email, code, bookingId });
  };

  // Start Webxpay payment for a booking
  const startWebxpayForBooking = async () => {
    if (!bookingId) throw new Error("Booking id not found");
    // If your backend path differs, adjust the line below:
    const webx = await postData(
      `payments/webxpay/bookings/${bookingId}/start`, // <<< endpoint
      {}
    );
    const actionUrl = webx?.actionUrl;
    const fields = webx?.fields;
    if (!actionUrl || !fields) throw new Error("Webxpay start response missing actionUrl/fields");
    submitWebxpayForm(actionUrl, fields); // redirects away
  };

  // Resend timer
  useEffect(() => {
    if (step !== 2) return;
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [step, resendTimer]);

  const mmss = useMemo(() => {
    const m = String(Math.floor(resendTimer / 60)).padStart(1, "0");
    const s = String(resendTimer % 60).padStart(2, "0");
    return `${m}min ${s}s`;
  }, [resendTimer]);

  const resend = async () => {
    if (resendTimer > 0) return;
    try {
      setBusy(true);
      await requestOtp();
    } finally {
      setBusy(false);
    }
  };

  // Wizard next/back
  const next = async () => {
    setSubmitted(true);
    try {
      if (step === 0) {
        const { firstName, lastName, email } = p1;
        if (!firstName || !lastName || !email) return;
        setSubmitted(false);
        setStep(1);
        return;
      }

      if (step === 1) {
        const { date, time, packageId } = p2;
        if (!date || !time || !packageId || !selectedPackage) return;

        setBusy(true);
        await createDraftBooking();
        await requestOtp();
        setBusy(false);

        setSubmitted(false);
        setStep(2);
        return;
      }

      if (step === 2) {
        if (otp.join("").length !== OTP_LEN) return;
        setBusy(true);
        await verifyOtp(); // booking -> AWAITING_PAYMENT
        setBusy(false);
        setSubmitted(false);
        setStep(3);
        return;
      }

      if (step === 3) {
        // Payment: start Webxpay and auto-redirect
        setBusy(true);
        await startWebxpayForBooking();
        setBusy(false);
        return; // navigation occurs
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  // ------- UI -------
  if (loading) return <div className="booking-shell p-5">Loading…</div>;
  if (err) return <div className="booking-shell p-5 text-danger">{err}</div>;

  return (
    <div className="booking-shell rounded-4 overflow-hidden shadow-sm">
      {/* Left panel */}
      <aside className="left-pane p-4 p-md-5">
        <h2 className="text-white fw-bold lh-sm mb-3 title-tight">
          BOOK YOUR VISIT
          <br />
          TO BRIEF GARDEN
        </h2>
        <p className="text-white-50 small mb-5">
          Choose your preferred package and fill in the details below to reserve your spot.
        </p>

        <ol className="wizard-list m-0 p-0">
          {STEPS.map((label, i) => (
            <li key={label} className={`wizard-item ${i === step ? "active" : i < step ? "done" : ""}`}>
              <span className="dot">{i < step ? "✓" : i + 1}</span>
              <span className="label">{label}</span>
              {i < STEPS.length - 1 && <span className="line" />}
            </li>
          ))}
        </ol>
      </aside>

      {/* Right panel */}
      <main className="right-pane p-4 p-md-5">
        <div className="border-start ps-md-5">
          <h4 className="fw-semibold text-dark-emphasis mb-4">{STEPS[step]}</h4>

          {/* Step 0 */}
          {step === 0 && (
            <>
              <div className="d-flex gap-4 mb-3">
                <label className="form-check">
                  <input
                    className="form-check-input me-2"
                    type="radio"
                    name="visitorType"
                    value="local"
                    checked={p1.visitorType === "local"}
                    onChange={handleP1}
                  />
                  <span>Local</span>
                </label>
                <label className="form-check">
                  <input
                    className="form-check-input me-2"
                    type="radio"
                    name="visitorType"
                    value="foreign"
                    checked={p1.visitorType === "foreign"}
                    onChange={handleP1}
                  />
                  <span>Foreign</span>
                </label>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">First Name *</label>
                  <input className="form-control" name="firstName" value={p1.firstName} onChange={handleP1} />
                  {submitted && !p1.firstName && <div className="text-danger small mt-1">First name is required.</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last Name *</label>
                  <input className="form-control" name="lastName" value={p1.lastName} onChange={handleP1} />
                  {submitted && !p1.lastName && <div className="text-danger small mt-1">Last name is required.</div>}
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label">Email *</label>
                <input type="email" className="form-control" name="email" value={p1.email} onChange={handleP1} />
                {submitted && !p1.email && <div className="text-danger small mt-1">Email is required.</div>}
              </div>

              <div className="mt-3">
                <label className="form-label">Contact Number</label>
                <input className="form-control" name="contactNumber" value={p1.contactNumber} onChange={handleP1} />
              </div>
            </>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-control" name="date" value={p2.date} onChange={handleP2} />
                  {submitted && !p2.date && <div className="text-danger small mt-1">Date is required.</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Time *</label>
                  <select className="form-select" name="time" value={p2.time} onChange={handleP2}>
                    <option value="">Select a time</option>
                    {(selectedPackage?.slotKeys || []).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {submitted && !p2.time && <div className="text-danger small mt-1">Time is required.</div>}
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label">Package *</label>
                <select
                  className="form-select"
                  name="packageId"
                  value={p2.packageId || selectedPackage?.code || ""}
                  onChange={handleP2}
                >
                  <option value="">Select a package</option>
                  {packages.map((p) => (
                    <option key={p._id} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {submitted && !p2.packageId && <div className="text-danger small mt-1">Package is required.</div>}
              </div>

              <div className="mt-3">
                <label className="form-label d-block">Count</label>
                <div className="d-flex flex-wrap gap-4">
                  <Counter label="Adults" value={p2.adults} onDec={() => dec("adults")} onInc={() => inc("adults")} min={1} />
                  <Counter label="Children" value={p2.children} onDec={() => dec("children")} onInc={() => inc("children")} min={0} />
                </div>
              </div>

              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="under4"
                  checked={p2.hasUnder4}
                  onChange={(e) =>
                    setP2((f) => ({
                      ...f,
                      hasUnder4: e.target.checked,
                      under4Count: e.target.checked ? Math.max(f.under4Count, 1) : 0,
                    }))
                  }
                />
                <label htmlFor="under4" className="form-check-label">
                  Select if any children are under 4 years old
                  <div className="text-muted small">(Children under 4 are free)</div>
                </label>
              </div>

              {p2.hasUnder4 && (
                <div className="mt-3">
                  <label className="form-label">How many under age 4?</label>
                  <Counter
                    value={p2.under4Count}
                    onDec={() => setP2((f) => ({ ...f, under4Count: Math.max(f.under4Count - 1, 1) }))}
                    onInc={() => setP2((f) => ({ ...f, under4Count: Math.min(f.under4Count + 1, f.children || 10) }))}
                    min={1}
                  />
                </div>
              )}
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              <p className="text-muted mb-4" style={{ maxWidth: 560 }}>
                We’ve sent a {OTP_LEN}-digit verification code to
                <strong> {p1.email || "(email)"}.</strong> Enter it below.
              </p>

              <div className="d-flex gap-3 mb-3">
                {Array.from({ length: OTP_LEN }).map((_, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    className="otp-input form-control text-center"
                    value={otp[i]}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKeyDown(i, e)}
                    inputMode="numeric"
                    maxLength={1}
                  />
                ))}
              </div>

              <div className="d-flex align-items-center gap-3">
                <button type="button" className="btn btn-link p-0 text-muted" disabled={resendTimer > 0 || busy} onClick={resend}>
                  RESEND
                </button>
                <span className="text-muted">{mmss}</span>
              </div>

              {quote && (
                <div className="mt-4 alert alert-info py-2 mb-0">
                  Amount due:{" "}
                  <strong>
                    {quote.currency} {quote.grossAmount}
                  </strong>
                </div>
              )}
            </>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="text-muted" style={{ maxWidth: 560 }}>
              <p className="mb-3">You’re almost done. Click <strong>Pay Now</strong> to continue to our secure Webxpay checkout.</p>
              {quote && (
                <p className="mb-4">
                  Amount due:{" "}
                  <strong>
                    {quote.currency} {quote.grossAmount}
                  </strong>
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            {step > 0 && (
              <button className="btn btn-outline-secondary" onClick={back} disabled={busy}>
                Back
              </button>
            )}
            <button className="btn btn-deepgreen px-4" onClick={next} disabled={busy}>
              {step === 3 ? (busy ? "Redirecting…" : "Pay Now") : busy ? "Please wait…" : "Next"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Counter({ label, value, onDec, onInc, min = 0 }) {
  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="btn btn-sm btn-ghost" onClick={onDec} disabled={value <= min} aria-label="decrease">
          −
        </button>
        <div className="count-box">{value}</div>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onInc} aria-label="increase">
          +
        </button>
      </div>
      {label && <div className="text-muted small mt-1 text-center">{label}</div>}
    </div>
  );
}

export default BookingWizard;
